import React, { useState, useMemo } from 'react';
import { 
  Zap, 
  ShieldCheck, 
  DollarSign, 
  Sparkles, 
  ArrowUpRight, 
  ArrowDownLeft, 
  FileText, 
  Upload, 
  Sliders, 
  Plus, 
  CheckCircle2, 
  Calendar, 
  Download, 
  TrendingUp, 
  RefreshCw,
  ExternalLink,
  Receipt,
  Globe
} from 'lucide-react';
import { getBusinessSettings, saveBusinessSettings } from '../utils/businessSettings';
import { getJurisdictionPreset, JURISDICTION_PRESETS } from '../utils/jurisdictionPresets';

interface QuickClarityDashboardProps {
  onSwitchToPowerOS: () => void;
  onOpenReconciler?: () => void;
  onOpenInvoices?: () => void;
  showToast?: (msg: string, type?: 'success' | 'info') => void;
}

interface QuickTransaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: 'inflow' | 'outflow';
  category: string;
  taxStatus: 'taxed' | 'deductible' | 'reserve';
}

export function QuickClarityDashboard({ 
  onSwitchToPowerOS, 
  onOpenReconciler, 
  onOpenInvoices,
  showToast 
}: QuickClarityDashboardProps) {
  const activeSettings = getBusinessSettings();
  const [jurisdiction, setJurisdiction] = useState<string>(activeSettings.jurisdiction || 'US');
  const preset = getJurisdictionPreset(jurisdiction);
  const currencySym = preset.currencySymbol || '$';

  // Quick Transactions State
  const [transactions, setTransactions] = useState<QuickTransaction[]>([
    {
      id: 'tx-1',
      date: 'Today',
      description: 'Client Retainer Wire (Advisory)',
      amount: 4500,
      type: 'inflow',
      category: 'Consulting',
      taxStatus: 'reserve'
    },
    {
      id: 'tx-2',
      date: 'Yesterday',
      description: 'Cloud Infrastructure & Servers',
      amount: 280,
      type: 'outflow',
      category: 'Software',
      taxStatus: 'deductible'
    },
    {
      id: 'tx-3',
      date: '3 days ago',
      description: 'Client Milestone 50% Upfront Deposit',
      amount: 6200,
      type: 'inflow',
      category: 'Client Project',
      taxStatus: 'reserve'
    },
    {
      id: 'tx-4',
      date: '4 days ago',
      description: 'Business Fiber Telecom & Tools',
      amount: 140,
      type: 'outflow',
      category: 'Utilities',
      taxStatus: 'deductible'
    }
  ]);

  // Quick Add Form State
  const [newDesc, setNewDesc] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const [newType, setNewType] = useState<'inflow' | 'outflow'>('inflow');
  const [newCategory, setNewCategory] = useState('Consulting');

  // Calculations
  const totalInflows = useMemo(() => {
    return transactions
      .filter((t) => t.type === 'inflow')
      .reduce((acc, t) => acc + t.amount, 0);
  }, [transactions]);

  const totalOutflows = useMemo(() => {
    return transactions
      .filter((t) => t.type === 'outflow')
      .reduce((acc, t) => acc + t.amount, 0);
  }, [transactions]);

  const vatRate = preset.vatRate || 0;
  const estimatedVat = vatRate > 0 ? Math.round(totalInflows * (vatRate / (100 + vatRate))) : 0;
  const netOperating = Math.max(0, totalInflows - estimatedVat - totalOutflows);
  const estimatedIncomeTax = Math.round(netOperating * (vatRate > 0 ? 0.18 : 0.22));
  const totalTaxReserve = estimatedVat + estimatedIncomeTax;
  const safeSpendableCash = Math.max(0, totalInflows - totalOutflows - totalTaxReserve);

  const handleAddTransaction = (e: React.FormEvent) => {
    e.preventDefault();
    const parsedAmount = parseFloat(newAmount);
    if (!newDesc.trim() || isNaN(parsedAmount) || parsedAmount <= 0) {
      if (showToast) showToast('Please enter a valid description and amount', 'info');
      return;
    }

    const newTx: QuickTransaction = {
      id: `tx-${Date.now()}`,
      date: 'Just now',
      description: newDesc.trim(),
      amount: parsedAmount,
      type: newType,
      category: newCategory,
      taxStatus: newType === 'inflow' ? 'reserve' : 'deductible'
    };

    setTransactions([newTx, ...transactions]);
    setNewDesc('');
    setNewAmount('');
    if (showToast) {
      showToast(
        newType === 'inflow' 
          ? `+${currencySym}${parsedAmount} logged! Tax reserve updated.` 
          : `-${currencySym}${parsedAmount} expense logged!`,
        'success'
      );
    }
  };

  const handleDropSimulation = () => {
    if (onOpenReconciler) {
      onOpenReconciler();
    } else {
      if (showToast) showToast('Bank statement dropped! 4 transactions auto-categorized.', 'success');
    }
  };

  const handleChangeJurisdiction = (code: string) => {
    setJurisdiction(code);
    saveBusinessSettings({
      ...activeSettings,
      jurisdiction: code,
      currencyCode: JURISDICTION_PRESETS[code]?.currencyCode || 'USD',
      currencySymbol: JURISDICTION_PRESETS[code]?.currencySymbol || '$'
    });
    if (showToast) showToast(`Region updated to ${JURISDICTION_PRESETS[code]?.name}`, 'info');
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto px-4 sm:px-6 py-6 animate-fade-in text-white">
      {/* Top Bar Banner: Mode & Authority Indicator */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-[#0E1214] border border-[#232B2E] rounded-2xl shadow-xs">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#14B8A6]/15 flex items-center justify-center text-[#14B8A6] shrink-0">
            <Zap size={20} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-white">Quick Clarity Workspace</span>
              <span className="text-[10px] font-mono font-bold bg-[#14B8A6]/20 text-[#14B8A6] px-2 py-0.5 rounded-full">
                Active
              </span>
            </div>
            <p className="text-xs text-gray-400">
              Tax rules synchronized with <strong className="text-gray-200">{preset.taxAuthorityLabel}</strong> ({preset.name})
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          {/* Quick Region Selector */}
          <div className="relative">
            <select
              value={jurisdiction}
              onChange={(e) => handleChangeJurisdiction(e.target.value)}
              className="bg-[#151B1D] border border-[#2A3438] text-xs text-gray-300 font-bold rounded-xl px-3 py-2 cursor-pointer focus:outline-hidden hover:border-gray-500 transition-colors"
            >
              <option value="US">🇺🇸 US (IRS)</option>
              <option value="GB">🇬🇧 UK (HMRC)</option>
              <option value="ZA">🇿🇦 SA (SARS)</option>
              <option value="EU">🇪🇺 EU (VAT)</option>
              <option value="AU">🇦🇺 AU (ATO)</option>
              <option value="IN">🇮🇳 IN (GST)</option>
            </select>
          </div>

          {/* Switch to Power OS Button */}
          <button
            type="button"
            onClick={onSwitchToPowerOS}
            className="px-3.5 py-2 bg-[#1A2226] hover:bg-[#232D32] border border-teal-500/30 hover:border-teal-400 text-teal-300 hover:text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-xs whitespace-nowrap"
          >
            <Sliders size={13} />
            <span>Switch to Power OS</span>
          </button>
        </div>
      </div>

      {/* The 3 Golden Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* 1. Safe to Spend Cash */}
        <div className="p-5 bg-gradient-to-br from-emerald-950/30 to-[#0F1416] border border-emerald-500/30 rounded-2xl shadow-lg flex flex-col justify-between">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">
                Safe to Spend Cash
              </span>
              <span className="text-[10px] font-mono font-bold bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full">
                Safe Draw
              </span>
            </div>
            <div className="text-3xl font-black font-mono text-emerald-300 tabular-nums pt-1">
              {currencySym}{safeSpendableCash.toLocaleString()}
            </div>
          </div>
          <div className="pt-3 border-t border-emerald-500/20 text-xs text-emerald-200/80 leading-relaxed">
            Free cash for owner salary and personal draw. All tax &amp; overhead obligations are fully covered.
          </div>
        </div>

        {/* 2. Locked Tax Reserve */}
        <div className="p-5 bg-gradient-to-br from-amber-950/30 to-[#0F1416] border border-amber-500/30 rounded-2xl shadow-lg flex flex-col justify-between">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-amber-400 uppercase tracking-wider">
                Locked Tax Reserve
              </span>
              <span className="text-[10px] font-mono font-bold bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full">
                Do Not Spend
              </span>
            </div>
            <div className="text-3xl font-black font-mono text-amber-300 tabular-nums pt-1">
              {currencySym}{totalTaxReserve.toLocaleString()}
            </div>
          </div>
          <div className="pt-3 border-t border-amber-500/20 text-xs text-amber-200/80 leading-relaxed">
            Reserved for {preset.taxAuthorityLabel}. Move this into your secondary tax holding account.
          </div>
        </div>

        {/* 3. Operating Overhead */}
        <div className="p-5 bg-[#0E1214] border border-[#232B2E] rounded-2xl shadow-lg flex flex-col justify-between">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                Operating Costs
              </span>
              <span className="text-[10px] font-mono font-bold bg-gray-800 text-gray-300 px-2 py-0.5 rounded-full">
                Deductible
              </span>
            </div>
            <div className="text-3xl font-black font-mono text-white tabular-nums pt-1">
              {currencySym}{totalOutflows.toLocaleString()}
            </div>
          </div>
          <div className="pt-3 border-t border-[#232B2E] text-xs text-gray-400 leading-relaxed">
            Monthly fixed overhead and deductible expenses reducing your net taxable income.
          </div>
        </div>
      </div>

      {/* 1-Click Drop Zone & Quick Add */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Quick Statement & Receipt Drop Zone */}
        <div 
          onClick={handleDropSimulation}
          className="p-5 bg-[#0E1214] border-2 border-dashed border-[#232B2E] hover:border-[#14B8A6]/60 rounded-2xl transition-all cursor-pointer flex flex-col items-center justify-center text-center group"
        >
          <div className="w-12 h-12 rounded-2xl bg-[#14B8A6]/10 flex items-center justify-center text-[#14B8A6] group-hover:scale-110 transition-transform mb-2.5">
            <Upload size={22} />
          </div>
          <h4 className="text-sm font-bold text-white group-hover:text-[#14B8A6] transition-colors">
            Drop Bank Statement or Receipt OCR
          </h4>
          <p className="text-xs text-gray-400 max-w-sm mt-1">
            Drag &amp; drop PDF, CSV, or a photo of a receipt. AzuraOS parses line items in browser with zero password sharing.
          </p>
          <div className="mt-3 flex items-center gap-2 text-[11px] font-mono text-teal-400">
            <span>Click to auto-parse statement</span>
            <span>→</span>
          </div>
        </div>

        {/* 10-Second Quick Entry Form */}
        <div className="p-5 bg-[#0E1214] border border-[#232B2E] rounded-2xl space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-gray-300 uppercase tracking-wider flex items-center gap-1.5">
              <Plus size={14} className="text-[#14B8A6]" />
              <span>Quick 1-Click Entry</span>
            </h4>
            <span className="text-[11px] text-gray-400">Auto-calculates tax hold</span>
          </div>

          <form onSubmit={handleAddTransaction} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                placeholder="Description (e.g. Client Wire)"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                className="col-span-2 bg-[#151B1D] border border-[#232B2E] rounded-xl px-3 py-2 text-xs text-white placeholder-gray-500 focus:outline-hidden focus:border-[#14B8A6]"
              />
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-mono">
                  {currencySym}
                </span>
                <input
                  type="number"
                  placeholder="Amount"
                  value={newAmount}
                  onChange={(e) => setNewAmount(e.target.value)}
                  className="w-full bg-[#151B1D] border border-[#232B2E] rounded-xl pl-6 pr-3 py-2 text-xs text-white font-mono placeholder-gray-500 focus:outline-hidden focus:border-[#14B8A6]"
                />
              </div>
              <div className="flex bg-[#151B1D] rounded-xl border border-[#232B2E] p-0.5">
                <button
                  type="button"
                  onClick={() => setNewType('inflow')}
                  className={`flex-1 text-[11px] font-bold rounded-lg py-1 transition-all ${
                    newType === 'inflow' 
                      ? 'bg-[#14B8A6] text-black shadow-xs' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Inflow
                </button>
                <button
                  type="button"
                  onClick={() => setNewType('outflow')}
                  className={`flex-1 text-[11px] font-bold rounded-lg py-1 transition-all ${
                    newType === 'outflow' 
                      ? 'bg-rose-500 text-white shadow-xs' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Expense
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="w-full py-2 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 shadow-sm active:scale-95"
            >
              <span>Add &amp; Update Tax Calculations</span>
              <Plus size={14} />
            </button>
          </form>
        </div>
      </div>

      {/* Recent Transactions List with Status Badges */}
      <div className="bg-[#0E1214] border border-[#232B2E] rounded-2xl overflow-hidden shadow-xs">
        <div className="p-4 border-b border-[#232B2E] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText size={15} className="text-[#14B8A6]" />
            <h4 className="text-xs font-bold text-white uppercase tracking-wider">
              Recent Transactions ({transactions.length})
            </h4>
          </div>
          <span className="text-[11px] text-gray-400 font-mono">
            Net Margin: {totalInflows > 0 ? (((totalInflows - totalOutflows) / totalInflows) * 100).toFixed(1) : 0}%
          </span>
        </div>

        <div className="divide-y divide-[#1A2226]">
          {transactions.map((tx) => (
            <div key={tx.id} className="p-3.5 flex items-center justify-between hover:bg-white/5 transition-colors">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${
                  tx.type === 'inflow' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-gray-800 text-gray-400'
                }`}>
                  {tx.type === 'inflow' ? <ArrowDownLeft size={16} /> : <ArrowUpRight size={16} />}
                </div>
                <div>
                  <div className="text-xs font-bold text-white">{tx.description}</div>
                  <div className="text-[11px] text-gray-400 flex items-center gap-2 mt-0.5">
                    <span>{tx.date}</span>
                    <span>·</span>
                    <span>{tx.category}</span>
                  </div>
                </div>
              </div>

              <div className="text-right">
                <div className={`text-xs font-black font-mono tabular-nums ${
                  tx.type === 'inflow' ? 'text-emerald-300' : 'text-gray-300'
                }`}>
                  {tx.type === 'inflow' ? '+' : '-'}{currencySym}{tx.amount.toLocaleString()}
                </div>
                <div className="text-[10px] font-mono text-gray-400 mt-0.5">
                  {tx.taxStatus === 'reserve' ? 'Tax Reserved' : 'Deductible'}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Power OS Unlocking Strip */}
      <div className="p-5 bg-gradient-to-r from-[#0E1214] via-[#121A1C] to-[#0E1214] border border-[#232B2E] rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="space-y-1 text-center sm:text-left">
          <h4 className="text-sm font-bold text-white flex items-center justify-center sm:justify-start gap-1.5">
            <Sparkles size={15} className="text-[#14B8A6]" />
            <span>Ready for full client operations?</span>
          </h4>
          <p className="text-xs text-gray-400 max-w-xl">
            Power OS mode unlocks client self-scheduling, official PDF invoicing, Google Calendar sync, and multi-provider AI financial agents.
          </p>
        </div>

        <button
          type="button"
          onClick={onSwitchToPowerOS}
          className="px-5 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-400 hover:from-teal-400 hover:to-emerald-300 text-black font-black text-xs rounded-xl shadow-md transition-all cursor-pointer whitespace-nowrap active:scale-95"
        >
          <span>Unlock All 12 Modules →</span>
        </button>
      </div>
    </div>
  );
}
